import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import BoardFormDialog from '../components/BoardFormDialog';
import { devicesService } from '../../services/devices';
import type { Board, BoardType, Device, GradingSystemRef } from '../../types/device';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { useGymAdmin } from '../context/gym-admin-context';

/**
 * The gym's boards. A board is gym-owned (a device is optional) and carries its
 * photo, grade system and — for wired boards — LED geometry. Each board shows
 * up as a tab on the gym profile.
 */
export default function BoardsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { loading: gymLoading, slug } = useCurrentGym();
  const { placePath } = useGymAdmin();

  const [boards, setBoards] = useState<Board[]>([]);
  const [boardTypes, setBoardTypes] = useState<BoardType[]>([]);
  const [gradingSystems, setGradingSystems] = useState<GradingSystemRef[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);

  const load = useCallback(async (gymSlug: string) => {
    setLoading(true);
    try {
      const [loadedBoards, types, systems, gymDevices] = await Promise.all([
        devicesService.listGymBoards(gymSlug),
        devicesService.listBoardTypes(),
        devicesService.listBoulderGradingSystems(gymSlug),
        devicesService.list(gymSlug),
      ]);
      setBoards(loadedBoards);
      setBoardTypes(types);
      setGradingSystems(systems);
      setDevices(gymDevices);
    } catch (error) {
      console.error('Failed to load boards:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const openNew = () => {
    setEditingBoard(null);
    setDialogOpen(true);
  };

  const openEdit = (board: Board) => {
    setEditingBoard(board);
    setDialogOpen(true);
  };

  const handleSaved = (saved: Board) => {
    setDialogOpen(false);
    setBoards((prev) => {
      const exists = prev.some((b) => b.id === saved.id);
      return exists ? prev.map((b) => (b.id === saved.id ? saved : b)) : [...prev, saved];
    });
    toast({ title: t('gymAdmin.boards.saved') });
  };

  const handleDelete = async (board: Board) => {
    if (!slug) return;
    if (!window.confirm(t('gymAdmin.boards.confirmDelete', { name: board.name }))) return;

    try {
      await devicesService.removeBoard(slug, board.id);
      setBoards((prev) => prev.filter((b) => b.id !== board.id));
      toast({ title: t('gymAdmin.boards.deleted') });
    } catch (error) {
      toast({
        title: t('gymAdmin.boards.deleteError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  if (gymLoading || loading) return <Skeleton className="h-96 w-full max-w-3xl" />;

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('gymAdmin.boards.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('gymAdmin.boards.subtitle')}</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="mr-2 size-4" />
          {t('gymAdmin.boards.new')}
        </Button>
      </div>

      {boards.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('gymAdmin.boards.empty')}
          </CardContent>
        </Card>
      ) : (
        boards.map((board) => (
          <Card key={board.id}>
            <CardContent className="flex flex-wrap items-center gap-4 p-4">
              {board.image_url ? (
                <img
                  src={board.image_url}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-md border object-cover"
                />
              ) : (
                <div className="h-16 w-16 shrink-0 rounded-md border bg-muted" />
              )}
              {/* The card body opens the board's dashboard; the buttons on the
                  right stay configuration. */}
              <Link to={placePath(`/boards/${board.id}`)} className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate font-medium hover:underline">{board.name}</h3>
                  <Badge variant="secondary">{board.board_type.name}</Badge>
                  {board.global && <Badge>{t('gymAdmin.boards.global')}</Badge>}
                  {!board.visible && (
                    <Badge variant="destructive">{t('gymAdmin.boards.hiddenBadge')}</Badge>
                  )}
                  {board.led_kit && (
                    <Badge variant="outline">{t('gymAdmin.boards.ledKitBadge')}</Badge>
                  )}
                </div>
              </Link>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" asChild>
                  <Link to={placePath(`/boards/${board.id}`)}>
                    <LayoutDashboard className="mr-2 size-4" />
                    {t('gymAdmin.boards.manage')}
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openEdit(board)}
                  aria-label={t('common.edit')}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => handleDelete(board)}
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {slug && (
        <BoardFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          gymSlug={slug}
          board={editingBoard}
          boardTypes={boardTypes}
          gradingSystems={gradingSystems}
          devices={devices}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
